<template>
  <div class="layer-widget-panel scrollbar">
    <div class="measurements-container">
      <div class="measurements">
        <div class="form-group hidden position">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">X</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-x"
                v-model="xPosition"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">Y</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="yPosition"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>

        <div class="form-group hidden size">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">W</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-width"
                v-model="width"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">H</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="height"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="extra-icons">
        <LockIcon v-show="isWidgetLocked" :locked="isWidgetLocked" />
        <VisibilityIcon :isVisible="isWidgetVisible" @clicked="updateVisibility" />
        <PixelIcon :usePixels="usePixels" @clicked="changeValuesMode" />
      </div>
    </div>

    <button type="button" class="button-2-secondary" @click="showCreateTableModal = true">
      <i class="material-icons-outlined align-middle">edit</i>
      Edit Table
    </button>

    <div class="form-group template-input">
      <label for="template-name">Name</label>
      <input
        class="form-control"
        type="text"
        id="template-name"
        placeholder="Name"
        v-model="value.object.name"
      />
    </div>

    <div class="form-group hidden size">
      <div class="form-row">
        <div class="input-group col" title="Time Interval (seconds)">
          <div class="input-label">
            <span class="input-group-text">Time</span>
          </div>
          <input
            type="number"
            class="form-control"
            id="layer-width"
            min="1"
            max="120"
            :disabled="isWidgetLockedByOverride"
            v-model="tableInterval"
          />
        </div>

        <div class="input-group col" title="Rows per page">
          <div class="input-label">
            <span class="input-group-text">Rows</span>
          </div>
          <input
            type="number"
            class="form-control"
            min="1"
            max="60"
            :disabled="isWidgetLockedByOverride"
            v-model="tableRows"
          />
        </div>
      </div>
    </div>

    <div class="sub-section-title">Background</div>

    <div class="form-group hidden">
      <div class="form-row">
        <color-picker
          input-el-id="heading-row-color"
          title="Background Color"
          :disabled="isWidgetLockedByOverride"
          v-model="tableBackgroundColor"
        />
      </div>
    </div>

    <div class="sub-section-title">Headings</div>

    <div class="form-group hidden">
      <div class="form-row">
        <color-picker
          input-el-id="heading-row-color"
          title="Background Color"
          :disabled="isWidgetLockedByOverride"
          v-model="headingBackgroundColor"
        />

        <div class="input-group col" title="Font Size">
          <div class="input-label">
            <span class="input-group-text">Size</span>
          </div>

          <input
            type="number"
            min="0"
            max="120"
            class="form-control"
            :disabled="isWidgetLockedByOverride"
            v-model="headingFontSize"
          />
        </div>
      </div>
    </div>

    <div class="form-group alignment d-flex">
      <div class="alignments form-inline">
        <div class="alignment-mode align-content-center align-middle">
          <input
            type="radio"
            id="heading-left-align"
            class="custom-control-input"
            hidden
            :value="ALIGN_MODES.LEFT_ALIGN"
            :disabled="isWidgetLockedByOverride"
            v-model="value.object.styles.heading.alignment"
          />
          <label for="heading-left-align" class="alignment-label">
            <i class="material-icons align-middle">format_align_left</i>
          </label>
        </div>

        <div class="alignment-mode">
          <input
            type="radio"
            id="heading-center-align"
            class="custom-control-input"
            hidden
            :value="ALIGN_MODES.CENTER_ALIGN"
            :disabled="isWidgetLockedByOverride"
            v-model="value.object.styles.heading.alignment"
          />
          <label for="heading-center-align" class="alignment-label">
            <i class="material-icons align-middle">format_align_center</i>
          </label>
        </div>

        <div class="alignment-mode">
          <input
            type="radio"
            id="heading-right-align"
            class="custom-control-input"
            hidden
            :value="ALIGN_MODES.RIGHT_ALIGN"
            :disabled="isWidgetLockedByOverride"
            v-model="value.object.styles.heading.alignment"
          />
          <label for="heading-right-align" class="alignment-label">
            <i class="material-icons align-middle">format_align_right</i>
          </label>
        </div>
      </div>
    </div>

    <div class="sub-section-title">Rows</div>

    <div class="form-group hidden">
      <div class="form-row">
        <color-picker
          input-el-id="body-row-color"
          title="Background Color"
          :disabled="isWidgetLockedByOverride"
          v-model="bodyBackgroundColor"
        />

        <div class="input-group col" title="Font Size">
          <div class="input-label">
            <span class="input-group-text">Size</span>
          </div>

          <input
            type="number"
            min="0"
            max="120"
            class="form-control"
            :disabled="isWidgetLockedByOverride"
            v-model="bodyFontSize"
          />
        </div>
      </div>
    </div>

    <div class="form-group alignment d-flex">
      <div class="alignments form-inline">
        <div class="alignment-mode align-content-center align-middle">
          <input
            type="radio"
            id="body-left-align"
            class="custom-control-input"
            hidden
            :value="ALIGN_MODES.LEFT_ALIGN"
            :disabled="isWidgetLockedByOverride"
            v-model="value.object.styles.body.alignment"
          />
          <label for="body-left-align" class="alignment-label">
            <i class="material-icons align-middle">format_align_left</i>
          </label>
        </div>

        <div class="alignment-mode">
          <input
            type="radio"
            id="body-center-align"
            class="custom-control-input"
            hidden
            :value="ALIGN_MODES.CENTER_ALIGN"
            :disabled="isWidgetLockedByOverride"
            v-model="value.object.styles.body.alignment"
          />
          <label for="body-center-align" class="alignment-label">
            <i class="material-icons align-middle">format_align_center</i>
          </label>
        </div>

        <div class="alignment-mode">
          <input
            type="radio"
            id="body-right-align"
            class="custom-control-input"
            hidden
            :value="ALIGN_MODES.RIGHT_ALIGN"
            :disabled="isWidgetLockedByOverride"
            v-model="value.object.styles.body.alignment"
          />
          <label for="body-right-align" class="alignment-label">
            <i class="material-icons align-middle">format_align_right</i>
          </label>
        </div>
      </div>
    </div>

    <div class="sub-section-title">Lines</div>

    <div class="form-group hidden">
      <div class="form-row">
        <color-picker
          input-el-id="heading-row-color"
          title="Lines Color"
          :disabled="isWidgetLockedByOverride"
          v-model="tableLinesColor"
        />

        <div class="input-group col" title="Lines Thickness">
          <div class="input-label">
            <span class="input-group-text">T</span>
          </div>

          <input
            type="number"
            min="0"
            max="20"
            class="form-control"
            :disabled="isWidgetLockedByOverride"
            v-model="tableLines"
          />
        </div>
      </div>
    </div>

    <TableWidgetModal
      v-if="showCreateTableModal"
      isEditMode
      :tableData="value.object"
      @closeModal="showCreateTableModal = false"
    />
  </div>
</template>

<script>
  import { Sketch } from 'vue-color';
  import ClickOutside from 'vue-click-outside';

  import { restrictToBounds } from '@/helpers/draggable';

  import ColorPicker from '@/components/ColorPicker.vue';
  import PixelIcon from '@/components/layoutDesigner/editor/settingPanel/PixelIcon.vue';
  import LockIcon from '@/components/layoutDesigner/editor/settingPanel/LockIcon.vue';
  import VisibilityIcon from '@/components/layoutDesigner/editor/settingPanel/VisibilityIcon.vue';
  import ImageMode from '@/components/templates/editor/settingPanel/formControls/ImageMode.vue';
  import FontSelector from '@/components/common/fontSelector/FontSelector.vue';
  import LocationPicker from '@/components/apps/weather/LocationPicker.vue';
  import BaseText from '@/components/common/ui/atoms/baseText/BaseText.vue';
  import TableWidgetModal from '@/components/apps/table/TableWidgetModal.vue';

  import { ALIGN_MODES } from '@/models/layoutDesigner';

  import widgetMixin from '@/components/templates/editor/settingPanel/widgetMixin';

  export default {
    name: 'TableWidgetPanel',

    mixins: [widgetMixin],

    directives: {
      ClickOutside,
    },

    components: {
      ColorPicker,
      ImageMode,
      PixelIcon,
      LockIcon,
      VisibilityIcon,
      FontSelector,
      LocationPicker,
      Sketch,
      BaseText,
      TableWidgetModal,
    },

    data() {
      return {
        showColorPicker: false,
        pickerColorKey: null,
        ALIGN_MODES,
        folderName: '',
        showLocation: false,
        showLocationPicker: false,
        showCreateTableModal: false,
      };
    },

    computed: {
      tableInterval: {
        get() {
          return this.value.object.styles.pagination.interval;
        },
        set(value) {
          this.$set(
            this.value.object.styles.pagination,
            'interval',
            restrictToBounds(Number(value), 0, 3600),
          );
        },
      },

      tableRows: {
        get() {
          return this.value.object.styles.pagination.per_page;
        },
        set(value) {
          this.$set(
            this.value.object.styles.pagination,
            'per_page',
            restrictToBounds(Number(value), 1, 60),
          );
        },
      },

      tableBackgroundColor: {
        get() {
          return this.value.object.styles.table.backgroundColor || '#FFFFFF';
        },
        set(value) {
          this.$set(this.value.object.styles.table, 'backgroundColor', value);
        },
      },

      tableLinesColor: {
        get() {
          return this.value.object.styles.table.linesColor || '#2D2D2D';
        },
        set(value) {
          this.$set(this.value.object.styles.table, 'linesColor', value);
        },
      },

      tableLines: {
        get() {
          return this.value.object.styles.table.lineWidth || 1;
        },
        set(value) {
          this.$set(
            this.value.object.styles.table,
            'lineWidth',
            restrictToBounds(Number(value), 0, 20),
          );
        },
      },

      headingFontSize: {
        get() {
          return this.value.object.styles.heading.fontSize || 20;
        },
        set(value) {
          this.$set(
            this.value.object.styles.heading,
            'fontSize',
            restrictToBounds(Number(value), 0, 120),
          );
        },
      },

      headingBackgroundColor: {
        get() {
          return this.value.object.styles.heading.backgroundColor || '#FFFFFF';
        },
        set(value) {
          this.$set(this.value.object.styles.heading, 'backgroundColor', value);
        },
      },

      bodyFontSize: {
        get() {
          return this.value.object.styles.body.fontSize || 16;
        },
        set(value) {
          this.$set(
            this.value.object.styles.body,
            'fontSize',
            restrictToBounds(Number(value), 0, 120),
          );
        },
      },

      bodyBackgroundColor: {
        get() {
          return this.value.object.styles.body.backgroundColor || '#FFFFFF';
        },
        set(value) {
          this.$set(this.value.object.styles.body, 'backgroundColor', value);
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .picker {
    position: absolute;
    top: 40px;
    right: 0;
    z-index: 20;
  }

  .layer-widget-panel {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: 0px;
    padding-right: 10px;

    .button-2-secondary {
      margin: 16px 0;
      width: 100%;

      &.disabled {
        background-color: $backgroundGrey2;
      }
    }
  }

  .measurements-container {
    padding-top: 6px;
  }

  .sub-section-title {
    margin-top: 8px;
  }

  .alignment {
    margin-top: 6px;
  }

  .alignments {
    display: flex;
    gap: 8px;

    .alignment-mode {
      width: 26px;
      margin: 0 2px;

      input:checked + label {
        color: $primaryText;
        background: #f5f5f5;
        padding: 16px;
      }

      input + label {
        width: 30px;
        height: 30px;
        border-radius: 4px;
        color: $secondaryText;
      }

      .alignment-label {
        cursor: pointer;
      }
    }
  }

  .form-group {
    .row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      margin: 0;
    }
  }

  .content-section {
    padding-top: 6px;

    .section-title {
      font-weight: bold;
      padding-top: 16px;
      font-size: 16px;
      border-top: 1px solid $lineGrey;
      margin-bottom: 10px;
    }
  }

  .font-color-container {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 1rem;

    .form-group {
      margin-bottom: 0;
      width: 100%;
    }

    .color-section {
      display: flex;
      position: relative;
      align-items: flex-end;
      gap: 10px;
      min-width: 140px;
    }
  }

  .color-button {
    background-color: $lineGrey;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    height: 40px;
    padding: 8px 6px 6px 6px;
    width: 40px;

    i {
      color: $tableText;
      font-size: 18px;
      height: 18px;
      width: 18px;
    }

    img {
      margin-top: -1px;
      height: 20px;
      width: 20px;
    }

    .color-bar {
      border-radius: 5px;
      height: 4px;
      width: 100%;
      background-color: black;
    }
  }

  label {
    margin-bottom: 4px;
  }

  .location-container {
    margin-top: 16px;
    width: 100%;
  }

  .remove-button {
    width: 100%;
    text-align: right;

    &:hover {
      cursor: pointer;
      color: $primaryRed;
    }
  }

  .location-text {
    display: flex;
    margin-bottom: 8px;
  }
</style>
